import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Bell, Search, ArrowDown, Globe, ArrowUp, CreditCard, MoreHorizontal, FilePlus, X } from 'lucide-react';
import { Screen } from '../../types';
import { PhoneFrame } from '../PhoneFrame';
import UsdcLogo from '../../assets/usd-coin-usdc-logo.svg';
import PkoLogo from '../../assets/pko.svg';

interface WithdrawFiatScreenProps {
  screen: Screen;
  onTabChange?: (activeTab: 'withdraw' | 'elixir-transfer' | 'complete') => void;
}

const iconMap = {
  'file-plus': FilePlus,
  'arrow-down': ArrowDown,
  'globe': Globe,
  'arrow-up': ArrowUp,
  'credit-card': CreditCard,
  'more-horizontal': MoreHorizontal
};

export const WithdrawFiatScreen: React.FC<WithdrawFiatScreenProps> = ({ screen, onTabChange }) => {
  const [activeTab, setActiveTab] = useState<'withdraw' | 'elixir-transfer' | 'complete'>('withdraw');

  const handleTabChange = (newTab: 'withdraw' | 'elixir-transfer' | 'complete') => {
    setActiveTab(newTab);
    onTabChange?.(newTab);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-8 py-16">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left Side - Mobile Interface */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <PhoneFrame>
            {activeTab === 'withdraw' && (
              <div className="p-6 h-full flex flex-col">
                {/* Top Navigation */}
                <div className="flex items-center justify-between mb-6">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                  <div className="flex items-center space-x-3">
                    <Bell className="w-5 h-5 text-gray-600" />
                    <Search className="w-5 h-5 text-gray-600" />
                  </div>
                </div>

                {/* Virtual Accounts */}
                <div className="space-y-4 mb-6">
                  {screen.virtualAccounts?.map((account, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                      className="bg-gray-50 rounded-lg p-4"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {account.type}
                      </h3>
                      
                      {account.accountNumber && (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Account Number:</span>
                            <span className="font-mono text-gray-900">{account.accountNumber}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Routing Number:</span>
                            <span className="font-mono text-gray-900">{account.routingNumber}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Beneficiary Name:</span>
                            <span className="text-gray-900">{account.beneficiaryName}</span>
                          </div>
                        </div>
                      )}
                      
                      {account.iban && (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">IBAN:</span>
                            <span className="font-mono text-gray-900">{account.iban}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">BIC:</span>
                            <span className="font-mono text-gray-900">{account.bic}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Beneficiary Name:</span>
                            <span className="text-gray-900">{account.beneficiaryName}</span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="grid grid-cols-4 gap-4 mb-6"
                >
                  {screen.actions?.map((action) => {
                    const IconComponent = iconMap[action.icon as keyof typeof iconMap] || MoreHorizontal;
                    const isWithdraw = action.name === "Withdraw";
                    
                    return (
                      <motion.button
                        key={action.name}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={isWithdraw ? () => handleTabChange('elixir-transfer') : undefined}
                        className={`flex flex-col items-center space-y-2 p-2 ${
                          isWithdraw ? 'cursor-pointer' : ''
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isWithdraw 
                            ? 'bg-blue-100 border-2 border-blue-500' 
                            : 'bg-gray-100'
                        }`}>
                          <IconComponent className={`w-6 h-6 ${
                            isWithdraw ? 'text-blue-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <span className={`text-xs text-center ${
                          isWithdraw ? 'text-blue-600 font-medium' : 'text-gray-600'
                        }`}>
                          {action.name}
                        </span>
                      </motion.button>
                    );
                  })}
                </motion.div>

                {/* Transaction History */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="flex-1"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Transaction History
                  </h3>
                  <div className="text-center text-gray-500 py-8">
                    {screen.depositHistory}
                  </div>
                </motion.div>
              </div>
            )}

            {activeTab === 'elixir-transfer' && (
              <div className="p-6 h-full flex flex-col">
                {/* PLN Withdrawal Header */}
                <div className="flex items-center justify-between mb-4">
                  <h1 className="text-xl font-bold text-black">PLN Withdrawal</h1>
                  <X className="w-5 h-5 text-gray-600" />
                </div>
                
                {/* From Account */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">From</h3>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <img src={UsdcLogo} alt="USDC" className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">USDC Account</p>
                      <p className="text-xs text-gray-600">Balance: $115.00</p>
                    </div>
                  </div>
                </div>

                {/* To Account */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">To</h3>
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center">
                      <img src={PkoLogo} alt="PKO" className="w-10 h-10" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">PKO Bank Polski</p>
                      <p className="text-xs text-gray-600">PL61109010140000071219812874</p>
                    </div>
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value="50.00"
                      readOnly
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-medium"
                    />
                    <span className="text-sm text-gray-600">PLN</span>
                  </div>
                </div>

                {/* Exchange Rate */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Exchange Rate</label>
                  <div className="text-sm text-gray-600">
                    1 USDC = 3.69 PLN
                  </div>
                </div>

                {/* Confirm Withdrawal Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleTabChange('complete')}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold text-sm mt-auto cursor-pointer"
                >
                  Confirm Withdrawal
                </motion.button>
              </div>
            )}

            {activeTab === 'complete' && (
              <div className="p-6 h-full flex flex-col items-center justify-center bg-white">
                {/* Close button */}
                <div className="flex justify-end w-full mb-8">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-gray-600 text-lg font-bold">×</span>
                  </div>
                </div>

                {/* Success Card */}
                <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-lg">
                  {/* Success Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xl">✓</span>
                    </div>
                  </div>

                  {/* Success Title */}
                  <h2 className="text-xl font-bold text-gray-900 text-center mb-6">Withdrawal Successful!</h2>

                  {/* Transaction Details */}
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Ref ID</span>
                      <span className="text-sm font-medium text-gray-900">123123123</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Method</span>
                      <span className="text-sm font-medium text-gray-900">Elixir Express</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                      <span className="text-sm text-gray-500">Time</span>
                      <span className="text-xs font-medium text-gray-900">{new Date().toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-gray-500">Amount</span>
                      <span className="text-sm font-medium text-gray-900">50 PLN</span>
                    </div>
                  </div>

                  {/* Download Receipt Button */}
                  <button className="w-full bg-white border border-gray-300 rounded-lg py-3 text-sm font-medium text-gray-900 hover:bg-gray-50 transition-colors">
                    Download Receipt
                  </button>
                </div>
              </div>
            )}
          </PhoneFrame>
        </motion.div>

        {/* Right Side - Info Panel */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          <div>
            <h2 className="text-3xl font-bold text-black mb-4">
              {screen.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              {screen.description}
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => handleTabChange('withdraw')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'withdraw'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Withdraw
            </button>
            <button
              onClick={() => handleTabChange('elixir-transfer')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'elixir-transfer'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Elixir Transfer
            </button>
            <button
              onClick={() => handleTabChange('complete')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'complete'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Complete
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'withdraw' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">
                Local Off-ramp
              </h3>
              <ul className="space-y-3">
                {screen.features?.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2"></div>
                    <span className="text-blue-800 text-sm leading-relaxed">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'elixir-transfer' && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex space-x-2">
                <div className="text-sm font-medium text-gray-600">
                  &lt;/&gt; Code
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm">
              <div className="space-y-1">
                {/* curl command line */}
                <div className="flex space-x-2">
                  <span className="text-blue-600 font-semibold">curl</span>
                  <span className="text-gray-600">--request</span>
                  <span className="text-green-600 font-semibold">POST</span>
                </div>
                
                {/* URL */}
                <div className="flex space-x-2">
                  <span className="text-gray-600">--url</span>
                  <span className="text-blue-600">https://api.across.money/v0/transfers</span>
                </div>
                
                {/* Headers */}
                <div className="flex space-x-2">
                  <span className="text-gray-600">--header</span>
                  <span className="text-green-600">'API-Key: sdg7h6sd58gh7dg'</span>
                </div>
                
                {/* Data flag */}
                <div className="flex space-x-2">
                  <span className="text-gray-600">--data</span>
                </div>
                
                {/* JSON opening brace */}
                <div className="flex">
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* source */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"source"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* source.currency */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"currency"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"usdc"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source.payment_rail */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"payment_rail"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"ethereum"</span>
                </div>
                
                {/* source closing */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-gray-800">{'}'}</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* destination */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"destination"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* destination.currency */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"currency"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"pln"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* destination.payment_rail */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"payment_rail"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"elixir"</span>
                </div>
                
                {/* destination closing */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-gray-800">{'}'}</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* amount */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"amount"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-orange-600">50.00</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* on_behalf_of */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"on_behalf_of"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"Satoshi Nakamoto"</span>
                </div>
                
                {/* JSON closing brace */}
                <div className="flex">
                  <span className="text-gray-800">{'}'}</span>
                </div>
              </div>
            </div>
            </div>
          )}

          {activeTab === 'complete' && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-4xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex space-x-2">
                <div className="text-sm font-medium text-gray-600">
                  &lt;/&gt; Code
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto">
              <div className="space-y-1">
                {/* JSON opening brace */}
                <div className="flex">
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* id */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"id"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"transfer_123"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* client_reference_id */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"client_reference_id"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"client_transfer_id_123"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* state */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"state"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"awaiting_funds"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* on_behalf_of */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"on_behalf_of"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"cust_123"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* amount */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"amount"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"13.55"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* developer_fee */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"developer_fee"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0.0"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"source"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* source.payment_rail */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"payment_rail"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"ethereum"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source.currency */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"currency"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"USDC"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source.from_address */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"from_address"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0xDEADBEEFDEADBEEFDEADBEEFDEADBEEFDEADBE"</span>
                </div>
                
                {/* source closing */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-gray-800">{'}'}</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* destination */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"destination"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* destination.payment_rail */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"payment_rail"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"elixir"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* destination.currency */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"currency"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"pln"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* destination.external_account_id */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"external_account_id"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"ext_123"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* destination.elixir_reference */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"elixir_reference"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"Elixir reference"</span>
                </div>
                
                {/* destination closing */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-gray-800">{'}'}</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source_deposit_instructions */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"source_deposit_instructions"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* source_deposit_instructions.payment_rail */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"payment_rail"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"ethereum"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source_deposit_instructions.amount */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"amount"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"13.55"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source_deposit_instructions.currency */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"currency"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"usdc"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source_deposit_instructions.from_address */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"from_address"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0xDEADBEEFDEADBEEFDEADBEEFDEADBEEFDEADBE"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* source_deposit_instructions.to_address */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"to_address"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0xCafebabeCAFEBABEcafebabecafebabe00000000"</span>
                </div>
                
                {/* source_deposit_instructions closing */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-gray-800">{'}'}</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"receipt"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-gray-800">{'{'}</span>
                </div>
                
                {/* receipt.initial_amount */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"initial_amount"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"13.55"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt.developer_fee */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"developer_fee"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0.0"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt.exchange_fee */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"exchange_fee"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0.0"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt.subtotal_amount */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"subtotal_amount"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"13.55"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt.gas_fee */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"gas_fee"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"0.0"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt.final_amount */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"final_amount"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"13.55</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* receipt.url */}
                <div className="flex space-x-1 pl-8">
                  <span className="text-blue-600">"url"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"https://dashboard.across.money/transaction/00000000-0000-0000-0000-000000000000/receipt/00000000-0000-0000-0000-000000000000"</span>
                </div>
                
                {/* receipt closing */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-gray-800">{'}'}</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* created_at */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"created_at"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"2025-10-14T00:00:00.000Z"</span>
                  <span className="text-gray-800">,</span>
                </div>
                
                {/* updated_at */}
                <div className="flex space-x-1 pl-4">
                  <span className="text-blue-600">"updated_at"</span>
                  <span className="text-gray-800">:</span>
                  <span className="text-green-600">"2025-10-14T00:00:00.000Z"</span>
                </div>
                
                {/* JSON closing brace */}
                <div className="flex">
                  <span className="text-gray-800">{'}'}</span>
                </div>
              </div>
            </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};
